import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EditLocationOutlinedIcon from "@mui/icons-material/EditLocationOutlined";
import LockIcon from "@mui/icons-material/Lock";
import PostAddIcon from "@mui/icons-material/PostAdd";
import PublicIcon from "@mui/icons-material/Public";
import { ImageList, ImageListItem, Tooltip } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import OutlinedInput from "@mui/material/OutlinedInput";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Zoom from "@mui/material/Zoom";
import { useTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { createNew, editEvent, setAlertMessage } from "../../redux/eventsSlice";
import Utils from "../../utils/Utils";
import Location from "../Location";
import FileUploader from "../commons/FileUploader";

const { postBorderColors, currencies, pathToFormattedDirectory } = Utils;

export default function EventCreationModal({
  profile,
  newEventModalOpened,
  setNewEventModalOpened,
  oldEvent,
  editEventFunction,
  setEditingEvent,
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const handleClose = () => setNewEventModalOpened(false);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [path, setPath] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [isCommercial, setIsCommercial] = useState(false);
  const [autoAcceptInvitationRequests, setAutoAcceptInvitationRequests] =
    useState(false);
  const [costs, setCosts] = useState({ amount: 0, currency: "EUR" });
  const [dateTime, setDateTime] = useState(dayjs(new Date()));
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [mapOpen, setMapOpen] = useState(false);
  const [imageUploaderOpen, setImageUploaderOpen] = useState(false);
  const [location, setLocation] = useState(profile?.defaultLocation);
  const [formPage, setFormPage] = useState(1);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!oldEvent?.name) return;
    setName(oldEvent?.name || "");
    setText(oldEvent?.description || "");
    setPath(
      profile.subscribedInterests
        .map((item) => item.numberedPath || item.path)
        .includes(oldEvent?.numberedPath || oldEvent?.path)
        ? oldEvent?.numberedPath || oldEvent?.path
        : ""
    );
    setImages(oldEvent?.images);
    setDateTime(dayjs(oldEvent?.dateTime || new Date()));
    setIsPrivate(oldEvent?.isPrivate || false);
    setIsCommercial(oldEvent?.isCommercial || false);
    setAutoAcceptInvitationRequests(
      oldEvent?.autoAcceptInvitationRequests || false
    );
    setCosts(oldEvent?.costs || { amount: 0, currency: "EUR" });
    setLocation(oldEvent?.location);
  }, [oldEvent, profile.subscribedInterests]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const event = {
      ...oldEvent,
      authorID: profile._id,
      path,
      costs: { amount: costs.amount || 0, currency: costs.currency },
      name,
      dateTime: dayjs(dateTime).toISOString(),
      location,
      description: text,
      images,
      isPrivate,
      isCommercial,
      autoAcceptInvitationRequests,
    };
    // create the formData to send to the server
    const formData = new FormData();
    formData.append("eventBody", JSON.stringify(event));
    imageFiles.forEach((image) =>
      formData.append("imageFiles", image, image.name)
    );

    if (
      !event.path ||
      !profile.subscribedInterests
        .map((item) => item.numberedPath || item.path)
        .includes(event.path)
    )
      return dispatch(
        setAlertMessage({
          message: t("noDirectory"),
          type: "error",
          show: true,
        })
      );
    if (!event.location || isNaN(event.location.lat))
      return dispatch(
        setAlertMessage({ message: t("noLocation"), type: "error", show: true })
      );
    if (!event.name)
      return dispatch(
        setAlertMessage({
          message: t("noEventName"),
          type: "error",
          show: true,
        })
      );

    if (!event.dateTime) {
      return dispatch(
        setAlertMessage({
          message: t("noEventDateOrTime"),
          type: "error",
          show: true,
        })
      );
    }
    if (new Date(event.dateTime) - new Date() < 0)
      return dispatch(
        setAlertMessage({
          message: t("pastDate"),
          type: "error",
          show: true,
        })
      );
    if (
      !event.costs ||
      isNaN(event.costs.amount) ||
      event.costs.amount < 0 ||
      !event.costs.currency
    )
      return dispatch(
        setAlertMessage({
          message: t("invalidCost"),
          type: "error",
          show: true,
        })
      );
    if (!event.description && event.images.length === 0)
      return dispatch(
        setAlertMessage({
          message: t("noDescriptionOrImage"),
          type: "error",
          show: true,
        })
      );
    if (!event.authorID)
      return dispatch(
        setAlertMessage({
          message: t("invalidPost"),
          type: "error",
          show: true,
        })
      );
    if (oldEvent?._id) {
      if (!editEventFunction) return dispatch(editEvent(formData));
      dispatch(editEventFunction(formData));
    } else dispatch(createNew(formData));
    setName("");
    setText("");
    setPath("");
    setCosts({ amount: 0, currency: "EUR" });
    setIsPrivate(false);
    setIsCommercial(false);
    setImages([]);
    setMapOpen(false);
    setEditingEvent({});
    setImageUploaderOpen(false);
    setImageFiles([]);
    return setNewEventModalOpened(false);
  };

  const handleUpload = (files) => {
    setImageFiles((prev) => prev.concat(files));
  };

  // build an array to display preview of both image urls or newly uploaded files
  const imageUrlOrFiles = images.concat(imageFiles);
  // remove either from the images links or image files
  const removeImage = (img) => {
    if (img.url) setImages(images.filter((item) => item.url !== img.url));
    else setImageFiles(imageFiles.filter((item) => item.path !== img.path));
  };

  return (
    <Modal
      aria-labelledby="create-new-event"
      aria-describedby={t("createNewEventAriaDescription")}
      open={newEventModalOpened}
      onClose={handleClose}
      closeAfterTransition
      disableScrollLock
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 250,
        },
      }}
      sx={{
        overflowY: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: "0.5rem 0", sm: "1rem 0" },
      }}
    >
      <Zoom in={newEventModalOpened}>
        <Paper
          elevation={2}
          sx={{
            zIndex: 1,
            display: "block",
            top: 0,
            maxWidth: { xs: "100vw", sm: "600px" },
            width: { xs: "95vw", sm: "100%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: { xs: 0, sm: 4 },
            p: { xs: 1, sm: 3 },
            margin: "auto",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Tabs
              value={tabIndex}
              onChange={(e, v) => setTabIndex(v)}
              centered
              sx={{ mb: 2 }}
            >
              <Tab label={t("settings")} />
              <Tab label={t("content")} />
            </Tabs>
            {tabIndex === 0 && (
              <>
                <Box sx={{ display: { xs: "block", sm: "flex" }, gap: 2 }}>
                  <FormControl sx={{ mb: 2 }} size="medium" fullWidth>
                    <InputLabel id="path-label">
                      {t("selectDirectory")}
                    </InputLabel>
                    <Select
                      labelId="path-label"
                      id="path"
                      value={path}
                      label={t("selectDirectory")}
                      onChange={(e) => setPath(e.target.value)}
                    >
                      {profile.subscribedInterests.map((item) => (
                        <MenuItem key={item.path} value={item.path}>
                          {item.name || pathToFormattedDirectory(item.path)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    display: { xs: "block", sm: "flex" },
                    gap: 2,
                    justifyContent: "space-between",
                  }}
                >
                  <Tooltip title={t("commercialDisclaimer")} placement="top">
                    <FormControlLabel
                      sx={{
                        pr: 2,
                      }}
                      alignItems="flex-start"
                      control={
                        <Checkbox
                          checked={isCommercial}
                          onChange={(e) => setIsCommercial(e.target.checked)}
                        />
                      }
                      label={t("commercial")}
                    />
                  </Tooltip>
                  <Tooltip title={t("privateEventTooltip")} placement="top">
                    <FormControlLabel
                      sx={{ borderRadius: 1, minWidth: 200 }}
                      control={
                        <Checkbox
                          checked={!isPrivate}
                          onChange={(e) => setIsPrivate(!e.target.checked)}
                          // icon={<PublicIcon fontSize="small" />}
                          // checkedIcon={<PublicIcon fontSize="small" />}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {!isPrivate ? (
                              <PublicIcon fontSize="small" />
                            ) : (
                              <LockIcon fontSize="small" />
                            )}
                          </ListItemIcon>
                          <Typography>
                            {!isPrivate ? t("public") : t("private")}
                          </Typography>
                        </Box>
                      }
                    />
                  </Tooltip>
                </Box>

                <Tooltip
                  title={t("autoAcceptInvitationRequestsTooltip")}
                  placement="top"
                >
                  <FormControlLabel
                    sx={{ borderRadius: 1, mb: 2, minWidth: 200 }}
                    control={
                      <Checkbox
                        checked={autoAcceptInvitationRequests}
                        onChange={(e) =>
                          setAutoAcceptInvitationRequests(e.target.checked)
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t("autoAcceptInvitationRequests")}
                      </Typography>
                    }
                  />
                </Tooltip>

                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel htmlFor="amount-field">
                      {t("amount")}
                    </InputLabel>
                    <OutlinedInput
                      id="amount-field"
                      type="number"
                      value={costs.amount}
                      onChange={(e) =>
                        setCosts({ ...costs, amount: e.target.value })
                      }
                      label={t("amount")}
                      // startAdornment={
                      //   <InputAdornment position="start">
                      //     <Select
                      //       value={costs.currency}
                      //       onChange={(e) => setCosts({ ...costs, currency: e.target.value })}
                      //       variant="standard"
                      //       disableUnderline
                      //       sx={{ minWidth: '60px' }}
                      //     >
                      //       {currencies.map((option) => (
                      //         <MenuItem key={option.name} value={option.name}>
                      //           {option.sign}
                      //         </MenuItem>
                      //       ))}
                      //     </Select>
                      //   </InputAdornment>
                      // }
                    />
                  </FormControl>
                </Box>

                <Box
                  sx={{ display: "flex", my: 0.5, mt: 1, flexWrap: "nowrap" }}
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={i18n.language}
                  >
                    <DateTimePicker
                      label={t("dateAndTime")}
                      sx={{ width: "100%" }}
                      value={dateTime}
                      onChange={(value) => setDateTime(value)}
                      format="LLL"
                    />
                  </LocalizationProvider>
                </Box>

                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{ borderRadius: 10, my: 2 }}
                  startIcon={<EditLocationOutlinedIcon />}
                  onClick={() => setMapOpen((prev) => !prev)}
                  fullWidth
                >
                  {t("editLocation")}
                </Button>
                {mapOpen && (
                  <Location location={location} setLocation={setLocation} />
                )}
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 10, m: 0.5 }}
                    onClick={() => setTabIndex(1)}
                  >
                    {t("next")}
                  </Button>
                </Box>
              </>
            )}
            {tabIndex === 1 && (
              <>
                <TextField
                  id="standard-textarea-name"
                  label={t("eventName")}
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <TextField
                  id="standard-textarea-desc"
                  label={t("eventDescription")}
                  multiline
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{ borderRadius: 10, mb: 2 }}
                  startIcon={<AddPhotoAlternateOutlinedIcon />}
                  onClick={() => setImageUploaderOpen((prev) => !prev)}
                  fullWidth
                >
                  {t("addImage")}
                </Button>
                <Box className="my-2">
                  <ImageList
                    variant="masonry"
                    cols={1}
                    gap={8}
                    sx={{ width: "100%" }}
                  >
                    {imageUrlOrFiles.map((img) => (
                      <ImageListItem key={img.path || img.url}>
                        <Box
                          key={img.path || img.url}
                          className="group"
                          sx={{
                            "& img": {
                              borderRadius: 1,
                              objectFit: "cover",
                              width: "100%",
                            },
                            position: "relative",
                          }}
                        >
                          <img
                            srcSet={
                              img.url ? img.url : URL.createObjectURL(img)
                            }
                            src={img.url ? img.url : URL.createObjectURL(img)}
                            alt={t("post")}
                            loading="lazy"
                          />
                          <div className="bg-white/25 transition-colors group-hover:bg-white/100 dark:bg-slate-900/25 dark:group-hover:bg-slate-900 absolute top-0 right-0 rounded-full">
                            <IconButton onClick={() => removeImage(img)}>
                              <CloseIcon />
                            </IconButton>
                          </div>
                        </Box>
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
                {imageUploaderOpen && (
                  <FileUploader
                    acceptedFileTypes={{ "image/*": [] }}
                    onFileSelect={handleUpload}
                    instructionText={t("dragAndDropImage")}
                  />
                )}
                <Box
                  sx={{
                    mt: 2,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    sx={{ borderRadius: 10, m: 0.5 }}
                    onClick={() => setTabIndex(0)}
                  >
                    {t("back")}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    type="submit"
                    sx={{ borderRadius: 10, m: 0.5 }}
                    startIcon={<PostAddIcon />}
                  >
                    {t("post")}
                  </Button>
                </Box>
              </>
            )}
          </form>
        </Paper>
      </Zoom>
    </Modal>
  );
}
